"use client";

import React, { useReducer, useRef, useEffect } from "react";
import { flushSync } from "react-dom";
import Moveable from "react-moveable";
import { ExternalLink, MapPin, X } from "lucide-react";
import Link from "next/link";
import { MissionSites } from "@/components";
import { MissionType, SiteType } from "@/types";
import { Button } from "@oreme/ui";
import { useMission } from "@/hooks";
import { useAtom } from "jotai";
import { activePanelAtom } from "@/store";
import { useTranslations } from "next-intl";

type MissionPanelProps = {
  mission: MissionType;
  sites: SiteType[];
};

function MissionPanel({ mission, sites }: Readonly<MissionPanelProps>) {
  const t = useTranslations("mission.MissionPanel");
  const { cancelMission } = useMission();

  const targetRef = useRef<HTMLDivElement>(null);
  const dragTargetRef = useRef<HTMLButtonElement>(null);
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const moveableRef = useRef<Moveable>(null);
  const [isDragged, toggleDraggedState] = useReducer((state) => {
    return !state;
  }, false);
  const [activePanel, setActivePanel] = useAtom(activePanelAtom);
  const zIndex: string = activePanel === "MissionPanel" ? "z-20" : "z-10";

  const handleMouseDown = (): void => {
    toggleDraggedState();
    setActivePanel("MissionPanel");
  };

  const handleMouseUp = (): void => {
    toggleDraggedState();
  };

  const handleClose = () => cancelMission();

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = 0;
    }
  }, [mission]);

  return (
    <>
      <div
        className={`absolute ${zIndex} left-2 lg:left-auto bottom-6 right-2 lg:right-6 flex flex-col min-w-80 lg:w-[400px] h-[384px] min-h-56 rounded-md border bg-popover shadow-xl animate__animated animate__fadeIn animate__faster`}
        ref={targetRef}
      >
        <header className="p-4 border-b">
          <div className="flex flex-row justify-between items-center">
            <button
              className={`flex flex-row flex-1 hover:cursor-grab data-[state="dragged"]:cursor-grabbing`}
              data-state={isDragged ? "dragged" : "default"}
              onMouseDown={handleMouseDown}
              onMouseUp={handleMouseUp}
              ref={dragTargetRef}
              type="button"
            >
              <h2 className="flex flex-row justify-between items-center text-lg font-semibold">
                {t("title")} {mission.mission_num2}
              </h2>
            </button>
            <Button
              size="icon"
              variant="ghost"
              onClick={handleClose}
              title={t("close")}
            >
              <X size={16} />
            </Button>
          </div>
          <div className="flex flex-row gap-2 mt-2">
            <div className="flex flex-col justify-center items-center">
              <MapPin size={24} />
            </div>
            <div className="flex flex-col">
              <p className="text-sm">{mission.lieu}</p>
              {mission.countries ? (
                <p className="text-xs">
                  {mission.countries.replaceAll(",", ", ")}
                </p>
              ) : null}
            </div>
          </div>
        </header>
        <div className="relative flex flex-col flex-1 p-0">
          <div ref={scrollRef} className="absolute inset-0 overflow-y-auto">
            {mission.title ? (
              <div className="p-4 border-t">
                <header className="flex flex-row justify-between items-center mb-2">
                  <h4 className="text-xs font-semibold uppercase text-muted-foreground">
                    {t("description")}
                  </h4>
                </header>
                <p className="leading-tight text-sm">{mission.title}</p>
              </div>
            ) : null}
            {(mission.responsable1 || mission.responsable2) && (
              <div className="p-4 border-t">
                <header className="flex flex-row justify-between items-center mb-2">
                  <h4 className="text-xs font-semibold uppercase text-muted-foreground">
                    {t("leadership")}
                  </h4>
                </header>
                <ul className="text-sm">
                  {mission.responsable1 && <li>{mission.responsable1}</li>}
                  {mission.responsable2 && <li>{mission.responsable2}</li>}
                </ul>
              </div>
            )}
            {mission.url && (
              <div className="p-4 border-t">
                <header className="flex flex-row justify-between items-center mb-2">
                  <h4 className="text-xs font-semibold uppercase text-muted-foreground">
                    {t("data")}
                  </h4>
                </header>
                <div className="flex flex-col gap-4">
                  <dl className="grid grid-cols-12">
                    <dt className="col-span-2 text-sm">{t("doi")}</dt>
                    <dd className="text-sm col-span-10 leading-tight">
                      <Link href={mission.url} target="_blank">
                        {mission.url}
                      </Link>
                    </dd>
                  </dl>
                  <p>
                    <Button
                      variant="default"
                      size="sm"
                      className="block"
                      asChild
                    >
                      <Link
                        href={mission.url}
                        target="_blank"
                        className="flex flex-1 items-center justify-center gap-2"
                      >
                        {t("accessData")}
                        <ExternalLink size={16} />
                      </Link>
                    </Button>
                  </p>
                </div>
              </div>
            )}
            <MissionSites sites={sites} />
          </div>
        </div>
      </div>
      <Moveable
        className="mission-modal-moveable-control-box"
        flushSync={flushSync}
        ref={moveableRef}
        target={targetRef}
        dragTarget={dragTargetRef}
        draggable={true}
        edge={true}
        origin={false}
        resizable={true}
        scalable={false}
        linePadding={10}
        transformOrigin="top left"
        hideDefaultLines={true}
        onRender={(e) => {
          e.target.style.cssText += e.cssText;
        }}
      />
    </>
  );
}

export { MissionPanel };
